<template>
  <div class="fc-workspace h-screen flex overflow-hidden bg-[#0c0c0e] text-white">
    <!-- 最左：导航 -->
    <aside
      class="w-[56px] sm:w-16 shrink-0 border-r border-white/[0.06] bg-[#111114] flex flex-col items-center py-4 gap-5"
    >
      <div
        class="text-sm font-black tracking-tight bg-gradient-to-br from-fuchsia-400 to-violet-500 bg-clip-text text-transparent"
      >
        Oii
      </div>
      <router-link
        to="/creative-hub"
        class="w-10 h-10 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center text-gray-300 transition-colors"
        title="创作中心"
      >
        <i class="fas fa-home"></i>
      </router-link>
      <button
        type="button"
        class="w-10 h-10 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center text-amber-300/90 hover:text-amber-200 transition-colors"
        title="AI 画布 Pro（嵌入 AI-CanvasPro，需本地 python server.py）"
        @click="goAiCanvasPro"
      >
        <i class="fas fa-border-all text-sm"></i>
      </button>
      <router-link
        :to="{ name: 'ConversationWorkflow' }"
        class="w-10 h-10 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center text-gray-400 hover:text-cyan-300 transition-colors"
        title="对话工作流（新建）"
      >
        <i class="fas fa-project-diagram text-sm"></i>
      </router-link>
      <button
        type="button"
        class="w-10 h-10 rounded-full border border-white/10 text-gray-400 hover:text-white hover:border-white/20 transition-colors"
        title="功能"
      >
        <i class="fas fa-circle-notch text-sm"></i>
      </button>
      <div class="flex-1"></div>
      <button
        type="button"
        class="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-600 to-pink-600 text-white shadow-lg shadow-fuchsia-900/40 flex items-center justify-center hover:opacity-90 transition-opacity"
        title="新建"
        @click="goNew"
      >
        <i class="fas fa-plus"></i>
      </button>
    </aside>

    <!-- 中：对话 -->
    <section
      class="w-[min(100%,420px)] sm:w-[400px] lg:w-[440px] shrink-0 border-r border-white/[0.06] flex flex-col bg-[#121215] min-h-0"
    >
      <header
        class="shrink-0 px-4 py-3 border-b border-white/[0.06] flex items-center justify-between gap-2"
      >
        <h1 class="text-xs sm:text-sm font-semibold text-gray-100 truncate flex-1" :title="taskTitle">
          {{ taskTitle }}
        </h1>
        <div class="flex items-center gap-1 shrink-0">
          <button
            type="button"
            class="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06] text-[11px]"
          >
            分享
          </button>
          <button
            type="button"
            class="p-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/[0.06]"
          >
            <i class="fas fa-ellipsis-h"></i>
          </button>
        </div>
      </header>

      <div
        ref="chatScroll"
        class="flex-1 overflow-y-auto px-4 py-4 space-y-3 scrollbar-thin min-h-0"
        @scroll.passive="onChatScroll"
      >
        <div
          v-if="chatMessages.length === 0"
          class="text-center text-gray-600 text-[11px] py-10 leading-relaxed px-2"
        >
          对话模式 · 流式回复<br />
          <span class="text-gray-500">在下方输入描述，或从创作中心带文案进入</span>
        </div>

        <template v-for="m in chatMessages">
          <div v-if="m.role === 'user'" :key="m.id" class="flex justify-end">
            <div
              class="max-w-[92%] rounded-2xl rounded-tr-md px-3 py-2.5 text-[11px] leading-relaxed bg-gradient-to-br from-fuchsia-600/90 to-violet-700/90 text-white shadow-lg shadow-fuchsia-900/20 space-y-2"
            >
              <div
                v-if="m.attachments && m.attachments.length"
                class="flex flex-wrap gap-2 justify-end"
              >
                <div v-for="a in m.attachments" :key="a.id" class="shrink-0">
                  <img
                    v-if="a.kind === 'image' && a.dataUrl"
                    :src="a.dataUrl"
                    :alt="a.name || '图片'"
                    class="max-h-28 max-w-[min(100%,200px)] rounded-lg object-cover border border-white/20"
                  />
                  <video
                    v-else-if="a.kind === 'video' && a.objectUrl"
                    :src="a.objectUrl"
                    controls
                    playsinline
                    class="max-h-32 max-w-full rounded-lg border border-white/20 bg-black/30"
                  />
                </div>
              </div>
              <p v-if="(m.content || '').trim()" class="whitespace-pre-wrap break-words text-right">
                {{ m.content }}
              </p>
              <p
                v-else-if="m.attachments && m.attachments.length"
                class="text-[10px] text-white/70 text-right"
              >
                （仅附件）
              </p>
            </div>
          </div>
          <div v-else :key="m.id" class="flex justify-start">
            <div
              class="stream-assistant-bubble relative max-w-[92%] rounded-2xl rounded-tl-md px-3.5 pt-3 text-[12px] sm:text-[13px] leading-[1.65] text-gray-200/95 bg-gradient-to-br from-white/[0.07] to-white/[0.03] border border-white/[0.1] shadow-[0_8px_32px_rgba(0,0,0,0.35)]"
              :class="(m.content || '').trim() && !m.streaming ? 'pb-11' : 'pb-3'"
            >
              <div
                v-if="(m.content || '').trim() && !m.streaming"
                class="absolute bottom-2 right-2 z-[1] flex items-center gap-1.5"
              >
                <button
                  type="button"
                  class="h-7 min-w-[4.5rem] px-2 rounded-lg bg-black/40 hover:bg-black/60 border border-white/12 text-gray-300 hover:text-white text-[10px] flex items-center justify-center gap-1 transition-colors"
                  title="导出为 Word"
                  @click.stop="exportAssistantWord(m)"
                >
                  <i class="fas fa-file-word text-[10px] text-blue-300"></i>
                  <span>Word</span>
                </button>
                <button
                  type="button"
                  class="h-7 min-w-[4.25rem] px-2 rounded-lg bg-black/40 hover:bg-black/60 border border-white/12 text-gray-300 hover:text-white text-[10px] flex items-center justify-center gap-1.5 transition-colors"
                  title="复制全文"
                  @click.stop="copyAssistantMessage(m)"
                >
                  <i class="far fa-copy text-[10px]"></i>
                  <span>复制</span>
                </button>
              </div>
              <div
                v-if="m.streaming && !(m.content || '').trim()"
                class="flex items-center gap-2 text-gray-500 text-xs py-0.5"
              >
                <span class="typing-dots" aria-hidden="true">
                  <span></span><span></span><span></span>
                </span>
                <span class="text-[11px] text-gray-500">正在回复</span>
              </div>
              <div v-else class="stream-body">
                <div
                  class="markdown-reply stream-prose"
                  v-html="assistantHtml(m.content)"
                ></div>
                <span
                  v-if="m.streaming && (m.content || '').length > 0"
                  class="stream-caret"
                  aria-hidden="true"
                ></span>
              </div>
            </div>
          </div>
        </template>
      </div>

      <footer class="shrink-0 border-t border-white/[0.06] p-3 bg-[#0f0f12]">
        <input
          ref="wsImageInput"
          type="file"
          class="sr-only"
          accept="image/*"
          multiple
          @change="onWorkspacePickImages"
        />
        <input
          ref="wsVideoInput"
          type="file"
          class="sr-only"
          accept="video/*"
          multiple
          @change="onWorkspacePickVideos"
        />
        <div
          v-if="pendingAttachments.length"
          class="mb-2 flex flex-wrap gap-2 rounded-xl border border-white/[0.08] bg-[#18181c] p-2"
        >
          <div
            v-for="a in pendingAttachments"
            :key="a.id"
            class="relative group rounded-lg border border-white/10 overflow-hidden bg-black/20"
          >
            <img
              v-if="a.kind === 'image'"
              :src="a.dataUrl"
              :alt="a.name"
              class="h-16 w-16 object-cover"
            />
            <video
              v-else
              :src="a.objectUrl"
              muted
              class="h-16 w-24 object-cover"
            />
            <button
              type="button"
              class="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 text-white text-[10px] leading-5 opacity-0 group-hover:opacity-100 transition-opacity"
              title="移除"
              @click="removePendingAttachment(a.id)"
            >
              ×
            </button>
          </div>
        </div>
        <textarea
          v-model="bottomInput"
          rows="3"
          :disabled="isStreaming"
          class="w-full rounded-xl bg-[#18181c] border border-white/[0.08] px-3 py-2 text-xs text-gray-200 placeholder-gray-600 resize-none outline-none focus:border-fuchsia-500/40 disabled:opacity-50"
          placeholder="自由画布 / 自由选择模型，快速生成图片或视频素材（可附带图片或视频）"
          @keydown.enter.exact.prevent="onEnterSend"
        ></textarea>
        <div class="mt-2 flex items-center justify-between gap-2">
          <div class="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              :disabled="isStreaming"
              class="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center text-gray-400 disabled:opacity-40"
              title="上传图片"
              @click="openWorkspaceImagePicker"
            >
              <i class="far fa-image text-xs"></i>
            </button>
            <button
              type="button"
              :disabled="isStreaming"
              class="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center text-gray-400 disabled:opacity-40"
              title="上传视频"
              @click="openWorkspaceVideoPicker"
            >
              <i class="fas fa-film text-xs"></i>
            </button>
            <button
              type="button"
              class="h-8 px-2.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-[10px] text-gray-300 flex items-center gap-1.5"
            >
              Nano Pro
              <i class="fas fa-chevron-down text-[8px] text-gray-500"></i>
            </button>
            <button
              type="button"
              class="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-gray-500"
            >
              <i class="fas fa-layer-group text-xs"></i>
            </button>
            <button
              type="button"
              class="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-gray-500"
            >
              <i class="fas fa-palette text-xs"></i>
            </button>
            <button
              type="button"
              class="w-8 h-8 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] text-gray-500"
            >
              <i class="far fa-smile text-xs"></i>
            </button>
          </div>
          <button
            type="button"
            :disabled="isStreaming"
            @click="onWorkspaceSubmit"
            class="shrink-0 h-10 pl-3 pr-2 rounded-xl bg-gradient-to-r from-pink-600 to-fuchsia-600 hover:from-pink-500 hover:to-fuchsia-500 flex items-center gap-2 text-white text-xs font-semibold shadow-lg shadow-fuchsia-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span class="tabular-nums">7</span>
            <span class="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <i v-if="!isStreaming" class="fas fa-arrow-up text-sm"></i>
              <i v-else class="fas fa-spinner fa-spin text-sm"></i>
            </span>
          </button>
        </div>
      </footer>
    </section>

    <!-- 右：画布 / 结果 -->
    <section class="flex-1 flex flex-col min-w-0 bg-[#08080a] relative">
      <div
        class="shrink-0 px-4 py-3 flex items-center justify-between border-b border-white/[0.04] bg-[#0a0a0c]/80 backdrop-blur-sm"
      >
        <div class="flex items-center gap-2 text-[10px]">
          <button
            type="button"
            class="px-2.5 py-1 rounded-lg bg-white/[0.08] text-gray-200"
          >
            总览
          </button>
          <button
            type="button"
            class="px-2.5 py-1 rounded-lg text-gray-500 hover:text-gray-300"
          >
            图片/视频
          </button>
        </div>
        <div
          class="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/[0.08]"
        >
          <i class="fas fa-gift text-amber-400 text-[10px]"></i>
          <span class="text-fuchsia-200 font-semibold">100 FREE</span>
        </div>
      </div>

      <div class="flex-1 overflow-auto p-4 min-h-0">
        <div class="grid grid-cols-2 gap-3 max-w-2xl mx-auto">
          <div
            v-for="(c, i) in canvasCards"
            :key="i"
            class="aspect-square rounded-2xl border border-white/[0.08] overflow-hidden relative group bg-gradient-to-br from-slate-800/80 to-slate-950"
          >
            <div class="absolute inset-0 opacity-60" :class="c.gradient"></div>
            <div
              class="absolute inset-0 flex items-center justify-center text-[10px] text-white/40 font-medium"
            >
              {{ c.label }}
            </div>
            <div
              v-if="c.badge"
              class="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-black/50 text-[9px] text-amber-200/90"
            >
              {{ c.badge }}
            </div>
          </div>
        </div>
      </div>

      <div
        class="pointer-events-none absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 pointer-events-auto"
      >
        <button
          type="button"
          class="px-4 py-2 rounded-full bg-[#1a1a1f] border border-white/10 text-[11px] text-gray-300 hover:bg-white/[0.08] shadow-xl"
        >
          添加画板
        </button>
        <button
          type="button"
          class="px-4 py-2 rounded-full bg-[#1a1a1f] border border-white/10 text-[11px] text-gray-300 hover:bg-white/[0.08] shadow-xl"
        >
          多选
        </button>
      </div>

      <div
        class="absolute bottom-6 right-4 flex items-center gap-1 rounded-xl bg-[#1a1a1f]/95 border border-white/10 px-2 py-1.5 shadow-xl"
      >
        <button type="button" class="p-1.5 text-gray-500 hover:text-white text-[10px]">
          <i class="far fa-comment-dots"></i>
        </button>
        <button type="button" class="p-1.5 text-gray-500 hover:text-white text-[10px]">
          <i class="fas fa-mouse-pointer"></i>
        </button>
        <button type="button" class="p-1.5 text-gray-500 hover:text-white text-[10px]">
          <i class="fas fa-hand-paper"></i>
        </button>
        <span class="w-px h-4 bg-white/10 mx-1"></span>
        <button type="button" class="px-1.5 text-gray-500 hover:text-white text-xs">−</button>
        <span class="text-[10px] text-gray-400 tabular-nums px-1">100%</span>
        <button type="button" class="px-1.5 text-gray-500 hover:text-white text-xs">+</button>
      </div>
    </section>
  </div>
</template>

<script>
import { Document, Packer } from 'docx'
import { saveAs } from 'file-saver'
import { markdownToDocxChildren } from '@/utils/markdownToDocx'
import { showErrorToast, showSuccessToast } from '@/utils/toast'
import { streamWorkspaceReply } from '@/utils/workspaceStreamChat'
import marked from 'marked'
import DOMPurify from 'dompurify'

marked.setOptions({
  gfm: true,
  breaks: true
})

const STORAGE_KEY = 'workspaceInitialPrompt'

/**
 * 逐字节奏：固定时间间隔，避免「前半慢、接口一结束突然变快」。
 * 仅当缓冲区积压较多时，同一间隔内多显示 1～2 个字，温和追平网络突刺。
 */
const TW_CHAR_INTERVAL_MS = 72
const TW_BACKLOG_SOFT = 20
const TW_BACKLOG_HARD = 64

/** 提问区附件上限（避免请求体过大） */
const WS_MAX_IMAGES = 8
const WS_MAX_VIDEOS = 3
const WS_MAX_IMAGE_BYTES = 6 * 1024 * 1024
const WS_MAX_VIDEO_BYTES = 100 * 1024 * 1024

function genId() {
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export default {
  name: 'FreeCanvasSimpleWorkspace',
  data() {
    return {
      userPrompt: '',
      bottomInput: '',
      chatMessages: [],
      isStreaming: false,
      streamAbort: null,
      initialStreamStarted: false,
      chatStickBottom: true,
      /** 逐字输出：缓冲区与读取位置（按 Unicode 码点推进） */
      _twBuf: '',
      _twReadPos: 0,
      _twTimer: null,
      _twApiDone: false,
      _twAssistantRef: null,
      /** 待发送的图片/视频（发送后写入对应 user 消息的 attachments） */
      pendingAttachments: [],
      canvasCards: [
        {
          gradient: 'from-cyan-900/50 to-blue-950',
          label: '冰晶素材',
          badge: '正在挑战'
        },
        {
          gradient: 'from-violet-900/50 to-purple-950',
          label: '武器图标',
          badge: ''
        },
        {
          gradient: 'from-emerald-900/40 to-teal-950',
          label: '宝石 UI',
          badge: '已通关'
        },
        {
          gradient: 'from-amber-900/40 to-orange-950',
          label: '光效边框',
          badge: ''
        }
      ]
    }
  },
  computed: {
    taskTitle() {
      const firstUser = this.chatMessages.find(m => m.role === 'user')
      const raw = (firstUser && firstUser.content) != null ? firstUser.content : this.userPrompt || ''
      const p = String(raw).trim()
      if (p) return p.length > 28 ? p.slice(0, 28) + '…' : p
      if (firstUser && firstUser.attachments && firstUser.attachments.length) {
        return '自由画布 · 带附件'
      }
      return '自由画布 · 对话'
    }
  },
  created() {
    this.loadPromptFromStorageOrQuery()
  },
  mounted() {
    this.$nextTick(() => {
      const p = (this.userPrompt || '').trim()
      if (p && !this.initialStreamStarted) {
        this.initialStreamStarted = true
        this.appendUserAndStream(p, false)
      }
    })
  },
  beforeDestroy() {
    this._revokeAllWorkspaceBlobUrls()
    if (this.streamAbort) {
      this.streamAbort.abort()
      this.streamAbort = null
    }
    this._cancelTypewriter()
  },
  methods: {
    _revokeAttachmentUrls(list) {
      ;(list || []).forEach(a => {
        if (a && a.objectUrl) URL.revokeObjectURL(a.objectUrl)
      })
    },
    _revokeAllWorkspaceBlobUrls() {
      this._revokeAttachmentUrls(this.pendingAttachments)
      this.chatMessages.forEach(m => this._revokeAttachmentUrls(m.attachments))
    },
    openWorkspaceImagePicker() {
      const el = this.$refs.wsImageInput
      if (el) {
        el.value = ''
        el.click()
      }
    },
    openWorkspaceVideoPicker() {
      const el = this.$refs.wsVideoInput
      if (el) {
        el.value = ''
        el.click()
      }
    },
    removePendingAttachment(id) {
      const a = (this.pendingAttachments || []).find(x => x.id === id)
      if (a && a.objectUrl) URL.revokeObjectURL(a.objectUrl)
      this.pendingAttachments = (this.pendingAttachments || []).filter(x => x.id !== id)
    },
    onWorkspacePickImages(e) {
      const input = e.target
      const files = input.files
      if (!files || !files.length) return
      let imgCount = (this.pendingAttachments || []).filter(a => a.kind === 'image').length
      const toRead = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file.type.startsWith('image/')) continue
        if (file.size > WS_MAX_IMAGE_BYTES) {
          showErrorToast(`单张图片请 ≤ ${WS_MAX_IMAGE_BYTES / 1024 / 1024}MB：${file.name}`)
          continue
        }
        if (imgCount >= WS_MAX_IMAGES) {
          showErrorToast(`最多添加 ${WS_MAX_IMAGES} 张图片`)
          break
        }
        imgCount += 1
        toRead.push(file)
      }
      input.value = ''
      if (!toRead.length) return
      Promise.all(
        toRead.map(
          file =>
            new Promise((resolve, reject) => {
              const r = new FileReader()
              r.onload = () =>
                resolve({
                  id: genId(),
                  kind: 'image',
                  name: file.name,
                  dataUrl: r.result
                })
              r.onerror = () => reject(new Error('read'))
              r.readAsDataURL(file)
            })
        )
      )
        .then(items => {
          this.pendingAttachments = [...(this.pendingAttachments || []), ...items]
        })
        .catch(() => showErrorToast('读取图片失败'))
    },
    onWorkspacePickVideos(e) {
      const input = e.target
      const files = input.files
      if (!files || !files.length) return
      let vidCount = (this.pendingAttachments || []).filter(a => a.kind === 'video').length
      const added = []
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file.type.startsWith('video/')) continue
        if (file.size > WS_MAX_VIDEO_BYTES) {
          showErrorToast(`单个视频请 ≤ ${WS_MAX_VIDEO_BYTES / 1024 / 1024}MB：${file.name}`)
          continue
        }
        if (vidCount >= WS_MAX_VIDEOS) {
          showErrorToast(`最多添加 ${WS_MAX_VIDEOS} 个视频`)
          break
        }
        vidCount += 1
        added.push({
          id: genId(),
          kind: 'video',
          name: file.name,
          objectUrl: URL.createObjectURL(file)
        })
      }
      input.value = ''
      if (added.length) {
        this.pendingAttachments = [...(this.pendingAttachments || []), ...added]
      }
    },
    async exportAssistantWord(m) {
      const text = (m && m.content) || ''
      if (!text.trim()) return
      try {
        const doc = new Document({
          creator: 'OpenSource Banana',
          title: '对话导出',
          sections: [
            {
              children: markdownToDocxChildren(text)
            }
          ]
        })
        const blob = await Packer.toBlob(doc)
        const name = `回复-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.docx`
        saveAs(blob, name)
        showSuccessToast('已导出 Word')
      } catch (e) {
        console.error(e)
        showErrorToast('导出失败，请稍后重试')
      }
    },
    async copyAssistantMessage(m) {
      const text = (m && m.content) || ''
      if (!text.trim()) return
      try {
        const htmlBody = DOMPurify.sanitize(marked.parse(text), {
          USE_PROFILES: { html: true }
        })
        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
table{border-collapse:collapse;width:100%;margin:.5em 0}
th,td{border:1px solid #666;padding:6px 10px;vertical-align:top}
th{background:#e8e8e8;font-weight:700}
</style></head><body>${htmlBody}</body></html>`
        const plain = text

        if (navigator.clipboard && navigator.clipboard.write) {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([plain], { type: 'text/plain' })
            })
          ])
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(plain)
        } else {
          const ta = document.createElement('textarea')
          ta.value = text
          ta.style.position = 'fixed'
          ta.style.left = '-9999px'
          document.body.appendChild(ta)
          ta.select()
          document.execCommand('copy')
          ta.remove()
        }
        showSuccessToast('已复制到剪贴板')
      } catch (e) {
        showErrorToast('复制失败，请手动选择文本复制')
      }
    },
    assistantHtml(content) {
      const raw = content == null ? '' : String(content)
      if (!raw) return ''
      try {
        const html = marked.parse(raw)
        return DOMPurify.sanitize(html, {
          USE_PROFILES: { html: true }
        })
      } catch (e) {
        const esc = raw
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
        return DOMPurify.sanitize(
          `<p class="whitespace-pre-wrap break-words">${esc}</p>`
        )
      }
    },
    loadPromptFromStorageOrQuery() {
      let p = ''
      try {
        p = sessionStorage.getItem(STORAGE_KEY) || ''
      } catch (e) {
        p = ''
      }
      const q = this.$route.query && this.$route.query.q
      if (!p && q != null && q !== '') {
        p = Array.isArray(q) ? q[0] : String(q)
      }
      this.userPrompt = p
    },
    scrollChatBottom() {
      this.chatStickBottom = true
      this.$nextTick(() => {
        const el = this.$refs.chatScroll
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    onChatScroll() {
      const el = this.$refs.chatScroll
      if (!el) return
      const threshold = 96
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight
      this.chatStickBottom = gap < threshold
    },
    maybeScrollChatBottom() {
      if (!this.chatStickBottom) return
      const el = this.$refs.chatScroll
      if (!el) return
      el.scrollTop = el.scrollHeight
    },
    _twPendingLen() {
      return (this._twBuf || '').length - (this._twReadPos || 0)
    },
    _twTakeCodePoints(s, fromIdx, maxCount) {
      let i = fromIdx
      let out = ''
      let n = 0
      while (n < maxCount && i < s.length) {
        const cp = s.codePointAt(i)
        const w = cp > 0xffff ? 2 : 1
        out += String.fromCodePoint(cp)
        i += w
        n += 1
      }
      return { nextIdx: i, text: out }
    },
    _resetTypewriterBuffer() {
      this._twBuf = ''
      this._twReadPos = 0
    },
    _cancelTypewriter() {
      if (this._twTimer != null) {
        clearTimeout(this._twTimer)
        this._twTimer = null
      }
    },
    _flushTypewriterRemainderTo(assistant) {
      const buf = this._twBuf || ''
      const rest = buf.slice(this._twReadPos || 0)
      if (rest) {
        this.$set(assistant, 'content', (assistant.content || '') + rest)
      }
      this._resetTypewriterBuffer()
    },
    _finishTypewriterTurn(assistant) {
      this._cancelTypewriter()
      assistant.streaming = false
      this.isStreaming = false
      this.streamAbort = null
      this._twAssistantRef = null
      this._twApiDone = false
      this._resetTypewriterBuffer()
      this.$nextTick(() => this.maybeScrollChatBottom())
    },
    _enqueueTypewriterChunk(chunk) {
      if (!chunk) return
      this._twBuf = (this._twBuf || '') + chunk
      this._scheduleTypewriterFrame()
    },
    _scheduleTypewriterFrame() {
      if (this._twTimer != null) return
      this._twTimer = setTimeout(() => {
        this._twTimer = null
        this._typewriterFrame()
      }, TW_CHAR_INTERVAL_MS)
    },
    _typewriterFrame() {
      const assistant = this._twAssistantRef
      if (!assistant) return

      const buf = this._twBuf || ''
      let pos = this._twReadPos || 0
      const pendingBefore = buf.length - pos
      let perTick = 1
      if (pendingBefore >= TW_BACKLOG_HARD) perTick = 3
      else if (pendingBefore >= TW_BACKLOG_SOFT) perTick = 2
      const { nextIdx, text } = this._twTakeCodePoints(buf, pos, perTick)
      this._twReadPos = nextIdx

      if (this._twReadPos > 12000) {
        this._twBuf = this._twBuf.slice(this._twReadPos)
        this._twReadPos = 0
      }

      if (text) {
        this.$set(assistant, 'content', (assistant.content || '') + text)
        this.maybeScrollChatBottom()
      }

      const pending = this._twPendingLen()
      if (pending > 0) {
        this._scheduleTypewriterFrame()
        return
      }
      if (!this._twApiDone) {
        return
      }
      this._finishTypewriterTurn(assistant)
    },
    goNew() {
      this.$router.push({ name: 'CreativeHub' })
    },
    goAiCanvasPro() {
      this.$router.push({ name: 'FreeCanvasWorkspace', query: { node: '1' } }).catch(() => {})
    },
    onEnterSend() {
      if (this.isStreaming) return
      const hasText = !!(this.bottomInput || '').trim()
      const hasFiles = (this.pendingAttachments || []).length > 0
      if (!hasText && !hasFiles) return
      this.onWorkspaceSubmit()
    },
    onWorkspaceSubmit() {
      const t = (this.bottomInput || '').trim()
      const hasFiles = (this.pendingAttachments || []).length > 0
      if (!t && !hasFiles) {
        showErrorToast('请输入内容或上传图片/视频')
        return
      }
      if (this.isStreaming) return
      this.appendUserAndStream(t, true)
    },
    appendUserAndStream(text, clearBottom) {
      const t = (text || '').trim()
      let atts = []
      if (clearBottom) {
        atts = (this.pendingAttachments || []).map(a => ({
          id: a.id,
          kind: a.kind,
          name: a.name,
          dataUrl: a.dataUrl,
          objectUrl: a.objectUrl
        }))
        this.pendingAttachments = []
      }
      if ((!t && !atts.length) || this.isStreaming) return

      const userMsg = {
        id: genId(),
        role: 'user',
        content: t || (atts.length ? ' ' : '')
      }
      if (atts.length) userMsg.attachments = atts
      this.chatMessages.push(userMsg)
      if (clearBottom) this.bottomInput = ''

      const history = this.chatMessages.slice()
      this.scrollChatBottom()
      this.runAssistantStream(history)
    },
    async runAssistantStream(history) {
      if (this.isStreaming) return
      this.isStreaming = true
      if (this.streamAbort) this.streamAbort.abort()
      this.streamAbort = new AbortController()

      const assistant = { id: genId(), role: 'assistant', content: '', streaming: true }
      this.chatMessages.push(assistant)
      this.scrollChatBottom()

      this._cancelTypewriter()
      this._resetTypewriterBuffer()
      this._twApiDone = false
      this._twAssistantRef = assistant

      try {
        await streamWorkspaceReply({
          messages: history,
          signal: this.streamAbort.signal,
          onDelta: chunk => {
            this._enqueueTypewriterChunk(chunk)
          }
        })
      } catch (e) {
        if (e && (e.name === 'AbortError' || e.message === 'aborted')) {
          this._cancelTypewriter()
          this._flushTypewriterRemainderTo(assistant)
          assistant.streaming = false
          this.isStreaming = false
          this.streamAbort = null
          this._twAssistantRef = null
          this._twApiDone = false
          this._resetTypewriterBuffer()
        } else {
          const msg = e?.message ? String(e.message) : '流式请求失败'
          showErrorToast(msg)
          this._twApiDone = true
          this._cancelTypewriter()
          this._flushTypewriterRemainderTo(assistant)
          if (!(assistant.content || '').trim()) {
            this.$set(assistant, 'content', `（${msg}）`)
          }
          assistant.streaming = false
          this.isStreaming = false
          this.streamAbort = null
          this._twAssistantRef = null
          this._resetTypewriterBuffer()
        }
      } finally {
        this.streamAbort = null
        if (this._twAssistantRef === assistant) {
          this._twApiDone = true
          if (this._twPendingLen() > 0) {
            this._scheduleTypewriterFrame()
          } else {
            this._cancelTypewriter()
            this._finishTypewriterTurn(assistant)
          }
        }
      }
    }
  }
}
</script>

<style scoped>
.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.12) transparent;
}

.stream-body {
  display: block;
  line-height: 1.65;
}

.stream-prose {
  font-variant-ligatures: none;
  text-rendering: optimizeLegibility;
}

.stream-assistant-bubble {
  position: relative;
  overflow: hidden;
}

.stream-assistant-bubble::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  border-radius: inherit;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
}

.stream-caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 2px;
  vertical-align: text-bottom;
  border-radius: 1px;
  background: linear-gradient(180deg, #e879f9, #a855f7);
  animation: stream-caret-blink 0.95s step-end infinite;
}

@keyframes stream-caret-blink {
  0%,
  45% {
    opacity: 1;
  }
  50%,
  100% {
    opacity: 0.12;
  }
}

.typing-dots {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 14px;
}

.typing-dots span {
  width: 5px;
  height: 5px;
  border-radius: 9999px;
  background: linear-gradient(135deg, #e879f9, #a855f7);
  opacity: 0.35;
  animation: typing-dot 1.15s ease-in-out infinite;
}

.typing-dots span:nth-child(2) {
  animation-delay: 0.18s;
}

.typing-dots span:nth-child(3) {
  animation-delay: 0.36s;
}

@keyframes typing-dot {
  0%,
  100% {
    transform: translateY(0);
    opacity: 0.35;
  }
  35% {
    transform: translateY(-4px);
    opacity: 1;
  }
}
</style>

<!-- v-html 生成的节点不受 scoped 影响，用页面根类名限定作用域 -->
<style>
.fc-workspace .markdown-reply {
  word-break: break-word;
}

.fc-workspace .markdown-reply p {
  margin: 0.35em 0;
}

.fc-workspace .markdown-reply p:first-child {
  margin-top: 0;
}

.fc-workspace .markdown-reply p:last-child {
  margin-bottom: 0;
}

.fc-workspace .markdown-reply table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
  font-size: 11px;
  line-height: 1.45;
}

.fc-workspace .markdown-reply th,
.fc-workspace .markdown-reply td {
  border: 1px solid rgba(255, 255, 255, 0.14);
  padding: 6px 8px;
  text-align: left;
  vertical-align: top;
}

.fc-workspace .markdown-reply th {
  background: rgba(255, 255, 255, 0.06);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.92);
}

.fc-workspace .markdown-reply tr:nth-child(even) td {
  background: rgba(0, 0, 0, 0.12);
}

.fc-workspace .markdown-reply ul,
.fc-workspace .markdown-reply ol {
  margin: 0.35em 0;
  padding-left: 1.25rem;
}

.fc-workspace .markdown-reply code {
  font-size: 0.92em;
  padding: 0.1em 0.35em;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.fc-workspace .markdown-reply pre {
  margin: 0.5rem 0;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow-x: auto;
  font-size: 11px;
}

.fc-workspace .markdown-reply pre code {
  padding: 0;
  border: 0;
  background: transparent;
}

.fc-workspace .markdown-reply blockquote {
  margin: 0.45rem 0;
  padding-left: 0.75rem;
  border-left: 3px solid rgba(168, 85, 247, 0.55);
  color: rgba(255, 255, 255, 0.75);
}

.fc-workspace .markdown-reply h1,
.fc-workspace .markdown-reply h2,
.fc-workspace .markdown-reply h3 {
  margin: 0.6em 0 0.35em;
  font-weight: 700;
  line-height: 1.3;
}

.fc-workspace .markdown-reply h1 {
  font-size: 1.05rem;
}

.fc-workspace .markdown-reply h2 {
  font-size: 1rem;
}

.fc-workspace .markdown-reply h3 {
  font-size: 0.95rem;
}
</style>
